import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Alert,
  Paper,
  IconButton,
  TextField,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  usePatchInviteMarkAsScannedByIdMutation,
  usePostInviteUploadUrlMutation,
  useGetInviteByIdQuery,
  useGetInviteByCodeByCodeQuery,
} from "../../redux/residentPortalApi";
import InviteDetails from "./InviteDetails";
import { Html5Qrcode } from "html5-qrcode";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import CloseIcon from "@mui/icons-material/Close";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import BadgeIcon from "@mui/icons-material/Badge";
import { isNative } from "../../utils/platform";

const ScanInvite: React.FC = () => {
  const [scannedId, setScannedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [lastAction, setLastAction] = useState<{
    action: "check-in" | "check-out";
    scannedAt: string;
  } | null>(null);
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [isInitializingCamera, setIsInitializingCamera] =
    useState<boolean>(false);
  // Manual code entry states
  const [codeInput, setCodeInput] = useState<string>("");
  const [codeSubmitted, setCodeSubmitted] = useState<boolean>(false);

  // First-visit optional scans (photo / ID / vehicle plate) - collected once, on the first check-in only
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [idScanFile, setIdScanFile] = useState<File | null>(null);
  const [vehiclePlateNumber, setVehiclePlateNumber] = useState<string>("");
  const [isUploadingScans, setIsUploadingScans] = useState<boolean>(false);
  const [checksError, setChecksError] = useState<string | null>(null);

  // In-page live camera capture (photo / ID), mirrors the QR scanner's embedded camera UX
  const [captureMode, setCaptureMode] = useState<"photo" | "id" | null>(null);
  const [isInitializingCaptureCamera, setIsInitializingCaptureCamera] =
    useState<boolean>(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [capturedPreviewUrl, setCapturedPreviewUrl] = useState<string | null>(
    null,
  );

  // Refs for QR scanner
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerDivRef = useRef<HTMLDivElement | null>(null);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const idScanInputRef = useRef<HTMLInputElement | null>(null);

  // Refs for live camera capture
  const captureVideoRef = useRef<HTMLVideoElement | null>(null);
  const captureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const captureStreamRef = useRef<MediaStream | null>(null);
  const pendingCaptureFileRef = useRef<File | null>(null);

  // API hooks
  const [markAsScanned, { isLoading: isMarkingAsScanned }] =
    usePatchInviteMarkAsScannedByIdMutation();
  const [getUploadUrl] = usePostInviteUploadUrlMutation();
  const {
    data: invite,
    isLoading: isLoadingInvite,
    error: inviteError,
  } = useGetInviteByIdQuery(
    { id: scannedId || "" },
    {
      skip: !scannedId,
      refetchOnMountOrArgChange: true,
    },
  );

  // Fetch by 6-digit access code when submitted
  const {
    data: inviteByCode,
    isLoading: isLoadingByCode,
    error: inviteByCodeError,
  } = useGetInviteByCodeByCodeQuery(
    { code: codeInput },
    {
      skip: !codeSubmitted || codeInput.length !== 6,
      refetchOnMountOrArgChange: true,
    },
  );

  // Handle successful fetch by code -> set scannedId to reuse existing flow
  useEffect(() => {
    if (inviteByCode && inviteByCode.id) {
      setScannedId(inviteByCode.id);
      setError(null);
      setSuccess(false);
      setCodeSubmitted(false);
    }
  }, [inviteByCode]);

  // Surface errors from fetch-by-code
  useEffect(() => {
    if (inviteByCodeError && codeSubmitted) {
      setError("Invite not found or invalid code. Please check and try again.");
    }
  }, [inviteByCodeError, codeSubmitted]);

  // Initialize and clean up QR scanner
  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current
          .stop()
          .catch((err) => console.error("Error stopping scanner:", err));
      }
      stopCaptureStream();
    };
  }, []);

  const stopCaptureStream = () => {
    captureStreamRef.current?.getTracks().forEach((track) => track.stop());
    captureStreamRef.current = null;
    if (captureVideoRef.current) captureVideoRef.current.srcObject = null;
  };

  // Opens the same in-page live camera used for QR scanning, for either the visitor photo or ID scan
  const startCapture = async (mode: "photo" | "id") => {
    setCaptureMode(mode);
    setCaptureError(null);
    setCapturedPreviewUrl(null);
    setIsInitializingCaptureCamera(true);

    try {
      if (isNative()) {
        const { Camera } = await import("@capacitor/camera");
        const check = await Camera.checkPermissions();

        if (check.camera !== "granted") {
          const request = await Camera.requestPermissions({
            permissions: ["camera"],
          });
          if (request.camera !== "granted") {
            throw new Error("permission_denied");
          }
        }
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("browser_not_supported");
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      captureStreamRef.current = stream;
      if (captureVideoRef.current) {
        captureVideoRef.current.srcObject = stream;
        await captureVideoRef.current.play();
      }
    } catch (err) {
      let errorMessage =
        "Failed to start camera. Please check camera permissions and try again.";

      if (err instanceof Error) {
        if (err.message === "browser_not_supported") {
          errorMessage =
            "Your browser does not support camera access. You can choose a file instead.";
        } else if (
          err.message === "permission_denied" ||
          err.message.includes("Permission denied") ||
          err.message.includes("permission")
        ) {
          errorMessage =
            "Camera access was denied. Please allow camera access, or choose a file instead.";
        } else if (
          err.message.includes("device not found") ||
          err.message.includes("Requested device not found")
        ) {
          errorMessage = "No camera detected. You can choose a file instead.";
        }
        console.error("Error starting capture camera:", err);
      }

      setCaptureError(errorMessage);
    } finally {
      setIsInitializingCaptureCamera(false);
    }
  };

  const cancelCapture = () => {
    stopCaptureStream();
    if (capturedPreviewUrl) URL.revokeObjectURL(capturedPreviewUrl);
    setCaptureMode(null);
    setCaptureError(null);
    setCapturedPreviewUrl(null);
    pendingCaptureFileRef.current = null;
  };

  const takeSnapshot = () => {
    const video = captureVideoRef.current;
    const canvas = captureCanvasRef.current;
    if (!video || !canvas || !video.videoWidth) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const filename = `${
          captureMode === "id" ? "id-scan" : "visitor-photo"
        }-${Date.now()}.jpg`;
        pendingCaptureFileRef.current = new File([blob], filename, {
          type: "image/jpeg",
        });
        setCapturedPreviewUrl(URL.createObjectURL(blob));
        // Freeze on the captured frame for review while the camera stream is released
        stopCaptureStream();
      },
      "image/jpeg",
      0.9,
    );
  };

  const retakeCapture = () => {
    if (capturedPreviewUrl) URL.revokeObjectURL(capturedPreviewUrl);
    setCapturedPreviewUrl(null);
    pendingCaptureFileRef.current = null;
    if (captureMode) startCapture(captureMode);
  };

  const confirmCapture = () => {
    if (pendingCaptureFileRef.current) {
      if (captureMode === "photo") setPhotoFile(pendingCaptureFileRef.current);
      else if (captureMode === "id")
        setIdScanFile(pendingCaptureFileRef.current);
    }
    if (capturedPreviewUrl) URL.revokeObjectURL(capturedPreviewUrl);
    setCaptureMode(null);
    setCapturedPreviewUrl(null);
    pendingCaptureFileRef.current = null;
  };

  const startQrScanner = async () => {
    setIsInitializingCamera(true);
    setError(null);

    try {
      if (isNative()) {
        const { Camera } = await import("@capacitor/camera");
        const check = await Camera.checkPermissions();

        if (check.camera !== "granted") {
          const request = await Camera.requestPermissions({
            permissions: ["camera"],
          });
          if (request.camera !== "granted") {
            throw new Error("permission_denied");
          }
        }
      }

      // Check if the browser supports getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("browser_not_supported");
      }

      setIsScanning(true);

      await new Promise((resolve) => setTimeout(resolve, 1000));

      const qrReaderElement = document.getElementById("qr-reader");
      if (!qrReaderElement) {
        setIsScanning(false);
        throw new Error("QR reader element not found");
      }

      qrReaderElement.innerHTML = "";

      try {
        const scanner = new Html5Qrcode("qr-reader");
        scannerRef.current = scanner;

        await scanner.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
          },
          (decodedText) => {
            // Successfully scanned QR code
            handleQrCodeResult(decodedText);
          },
          undefined,
        );
      } catch (scannerError) {
        console.error("Error initializing scanner:", scannerError);
        throw new Error(`Failed to initialize scanner: ${scannerError}`);
      }

      // isScanning is already set to true earlier
      setIsInitializingCamera(false);
    } catch (err) {
      console.error("Error starting QR scanner:", err);

      // Provide more specific error messages based on the error
      let errorMessage =
        "Failed to start camera. Please check camera permissions and try again.";

      if (err instanceof Error) {
        if (err.message === "browser_not_supported") {
          errorMessage =
            "Your browser does not support camera access. Please try using a modern browser like Chrome, Firefox, or Safari.";
        } else if (err.message === "permission_denied") {
          errorMessage =
            "Camera permission was denied. Please enable camera permission for this app and try again.";
        } else if (
          err.message.includes("Permission denied") ||
          err.message.includes("permission")
        ) {
          errorMessage =
            "Camera access was denied. Please allow camera access in your browser settings.";
        } else if (
          err.message.includes("device not found") ||
          err.message.includes("Requested device not found")
        ) {
          errorMessage =
            "No camera detected. Please ensure your device has a working camera.";
        } else if (err.message.includes("QR reader element not found")) {
          errorMessage =
            "Camera initialization failed. Please refresh the page and try again.";
        }

        // Log detailed error for debugging
        console.error("Detailed error:", {
          message: err.message,
          name: err.name,
          stack: err.stack,
        });
      }

      setError(errorMessage);
      setIsScanning(false);
      setIsInitializingCamera(false);
    }
  };

  const stopQrScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        setIsScanning(false);
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
  };

  const handleQrCodeResult = (result: string) => {
    // Extract invite ID from QR code result
    // The QR code might contain a full URL or just the ID
    let extractedId = result.trim();

    // Check if the result is a URL containing an ID
    try {
      if (extractedId.includes("/")) {
        // Try to extract ID from URL path
        const url = new URL(extractedId);
        const pathParts = url.pathname.split("/");
        // Get the last part of the path which should be the ID
        const potentialId = pathParts[pathParts.length - 1];

        // If it's a valid ID format (non-empty string without special chars)
        if (potentialId && /^[a-zA-Z0-9_-]+$/.test(potentialId)) {
          extractedId = potentialId;
        }
      }
    } catch (err) {
      // If URL parsing fails, just use the original string
      console.log("Not a URL, using raw string as ID");
    }

    if (extractedId) {
      // Show success message for QR scan before stopping scanner
      const successMessage = document.createElement("div");
      successMessage.style.position = "absolute";
      successMessage.style.top = "0";
      successMessage.style.left = "0";
      successMessage.style.width = "100%";
      successMessage.style.height = "100%";
      successMessage.style.display = "flex";
      successMessage.style.flexDirection = "column";
      successMessage.style.alignItems = "center";
      successMessage.style.justifyContent = "center";
      successMessage.style.backgroundColor = "rgba(0, 128, 0, 0.7)";
      successMessage.style.color = "white";
      successMessage.style.zIndex = "10";
      successMessage.style.borderRadius = "8px";

      const icon = document.createElement("div");
      icon.innerHTML = "✓";
      icon.style.fontSize = "48px";
      icon.style.marginBottom = "10px";

      const text = document.createElement("div");
      text.textContent = "QR Code Scanned Successfully";
      text.style.fontSize = "16px";

      successMessage.appendChild(icon);
      successMessage.appendChild(text);

      const qrReader = document.getElementById("qr-reader");
      if (qrReader) {
        qrReader.appendChild(successMessage);

        // Remove the success message and stop scanner after a short delay
        setTimeout(() => {
          qrReader.removeChild(successMessage);
          stopQrScanner();

          setScannedId(extractedId);
          setError(null);
          setSuccess(false);
        }, 1500);
      } else {
        // If we can't show the visual indicator, just stop the scanner immediately
        stopQrScanner();
        setScannedId(extractedId);
        setError(null);
        setSuccess(false);
      }
    } else {
      stopQrScanner();
      setError("Invalid QR code. Please try again.");
    }
  };

  // Only a visitor's very first check-in offers the optional photo/ID/plate scans
  const isFirstVisit =
    Boolean(invite) &&
    (invite!.scanCount ?? 0) === 0 &&
    invite!.currentStatus !== "checked_in";

  const uploadScanFile = async (file: File): Promise<string | undefined> => {
    const { uploadUrl, fileUrl } = await getUploadUrl({
      body: { filename: file.name, contentType: file.type },
    }).unwrap();
    if (!uploadUrl || !fileUrl) return undefined;
    await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type },
    });
    return fileUrl;
  };

  const handleVerify = async () => {
    if (!scannedId) return;

    if (isFirstVisit && !photoFile && !idScanFile) {
      setChecksError(
        "Please scan the visitor's face or their ID/Passport/License before checking in.",
      );
      return;
    }

    try {
      let photoUrl: string | undefined;
      let idScanUrl: string | undefined;

      if (isFirstVisit && (photoFile || idScanFile)) {
        setIsUploadingScans(true);
        try {
          if (photoFile) photoUrl = await uploadScanFile(photoFile);
          if (idScanFile) idScanUrl = await uploadScanFile(idScanFile);
        } finally {
          setIsUploadingScans(false);
        }
      }

      const result = await markAsScanned({
        id: scannedId,
        body: isFirstVisit
          ? {
              photoUrl,
              idScanUrl,
              vehiclePlateNumber: vehiclePlateNumber.trim() || undefined,
            }
          : {},
      }).unwrap();
      setChecksError(null);
      setLastAction(
        result.action
          ? {
              action: result.action,
              scannedAt: result.scannedAt ?? new Date().toISOString(),
            }
          : null,
      );
      setSuccess(true);
      setPhotoFile(null);
      setIdScanFile(null);
      setVehiclePlateNumber("");
      // Cache is updated directly from the mutation response (see residentPortalApi.ts),
      // so no manual refetch is needed here.
    } catch (err) {
      setError("Failed to verify invite. Please try again.");
      console.error("Verification error:", err);
    }
  };

  const handleClear = () => {
    setScannedId(null);
    setError(null);
    setSuccess(false);
    setLastAction(null);
    setCodeInput("");
    setCodeSubmitted(false);
    setPhotoFile(null);
    setIdScanFile(null);
    setVehiclePlateNumber("");
    setChecksError(null);
  };

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", p: 2 }}>
      <Typography variant="h5" gutterBottom color="#345678">
        Scan & Verify Visitor Invites
      </Typography>

      <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="body1" gutterBottom>
          You can scan the QR code or enter the 6-digit access code below
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose one option: scanning will auto-detect the code; manual entry
          works if the visitor doesn't have a smartphone.
        </Typography>

        {/* Manual code entry - improved UI */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="overline"
            sx={{ color: "#345678", fontWeight: 700, letterSpacing: ".08em" }}
          >
            Option A: Enter Access Code
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              borderColor: "#cfe0ff",
              backgroundColor: "#f5f9ff",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 2,
              }}
            >
              <TextField
                value={codeInput}
                onChange={(e) => {
                  const digitsOnly = e.target.value
                    .replace(/\D/g, "")
                    .slice(0, 6);
                  setCodeInput(digitsOnly);
                }}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 6,
                }}
                sx={{
                  minWidth: 240,
                  flexGrow: 1,
                  "& .MuiOutlinedInput-root": {
                    height: 56,
                    alignItems: "center",
                  },
                  "& input": {
                    fontSize: "1.5rem",
                    textAlign: "center",
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                  },
                }}
                placeholder="Access Code"
              />
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<VpnKeyIcon />}
                disabled={codeInput.length !== 6 || Boolean(isLoadingByCode)}
                onClick={() => {
                  setError(null);
                  setSuccess(false);
                  setCodeSubmitted(true);
                }}
                sx={{ minWidth: 180, height: 56, px: 3, fontWeight: 600 }}
              >
                {isLoadingByCode ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Verify by Code"
                )}
              </Button>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1.5, display: "block" }}
            >
              Tip: Ask the visitor for the 6-digit access code if they can't
              show the QR code.
            </Typography>
          </Paper>
        </Box>

        <Divider sx={{ my: 3 }}>
          <Chip label="OR" />
        </Divider>
        <Typography
          variant="overline"
          sx={{ color: "#345678", fontWeight: 700, letterSpacing: ".08em" }}
        >
          Option B: Scan QR Code
        </Typography>

        <Box sx={{ position: "relative", width: "100%", mb: 3 }}>
          {!isScanning ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                border: "2px dashed #ccc",
                borderRadius: 2,
                p: 4,
                backgroundColor: "#f9f9f9",
                minHeight: "250px",
              }}
            >
              {isInitializingCamera ? (
                <>
                  <CircularProgress
                    size={60}
                    sx={{ color: "#345678", mb: 2 }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                    textAlign="center"
                  >
                    Initializing camera...
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    textAlign="center"
                  >
                    Please allow camera access when prompted
                  </Typography>
                  <Box sx={{ mt: 2, width: "100%", maxWidth: 300 }}>
                    <Box
                      sx={{
                        width: "100%",
                        height: 4,
                        backgroundColor: "#e0e0e0",
                        borderRadius: 2,
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          bottom: 0,
                          backgroundColor: "#345678",
                          animation: "pulse 1.5s infinite ease-in-out",
                          "@keyframes pulse": {
                            "0%": { width: "0%" },
                            "50%": { width: "100%" },
                            "100%": { width: "0%" },
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </>
              ) : (
                <>
                  <QrCodeScannerIcon
                    sx={{ fontSize: 60, color: "#345678", mb: 2 }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                    textAlign="center"
                  >
                    Camera access is required to scan QR codes
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={startQrScanner}
                    startIcon={<QrCodeScannerIcon />}
                    sx={{ mt: 2 }}
                  >
                    Start Camera
                  </Button>
                </>
              )}
            </Box>
          ) : (
            <Box sx={{ position: "relative" }}>
              <div
                id="qr-reader"
                ref={scannerDivRef}
                style={{
                  width: "100%",
                  minHeight: "300px",
                }}
              ></div>
              <IconButton
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  backgroundColor: "rgba(255,255,255,0.8)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.9)" },
                  zIndex: 10,
                }}
                onClick={stopQrScanner}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {lastAction?.action === "check-out"
              ? `Visitor checked OUT at ${new Date(
                  lastAction.scannedAt,
                ).toLocaleTimeString("en-GB")}`
              : lastAction?.action === "check-in"
              ? `Visitor checked IN at ${new Date(
                  lastAction.scannedAt,
                ).toLocaleTimeString("en-GB")}`
              : "Invite successfully verified!"}
          </Alert>
        )}
      </Paper>

      {isLoadingInvite && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {inviteError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Invite not found or invalid ID. Please check and try again.
        </Alert>
      )}

      {invite && (
        <Card raised sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Invite Details
            </Typography>
            <InviteDetails
              invite={invite}
              showBanner={true}
              showVisitLog={true}
            />

            {isFirstVisit && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mt: 3,
                  borderRadius: 2,
                  borderColor: "#cfe0ff",
                  backgroundColor: "#f5f9ff",
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: "#345678",
                    fontWeight: 700,
                    letterSpacing: ".08em",
                  }}
                >
                  First Visit - Required Checks
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Captured once, on the visitor's first check-in. At least one
                  of Visitor Photo or Scan ID/Passport/License is required.
                  Vehicle plate number is optional.
                </Typography>
                {checksError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {checksError}
                  </Alert>
                )}

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    onClick={() => {
                      setChecksError(null);
                      startCapture("photo");
                    }}
                  >
                    {photoFile ? "Photo Selected" : "Visitor Photo"}
                  </Button>

                  <input
                    ref={idScanInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => setIdScanFile(e.target.files?.[0] ?? null)}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<BadgeIcon />}
                    onClick={() => {
                      setChecksError(null);
                      startCapture("id");
                    }}
                  >
                    {idScanFile
                      ? "ID Scan Selected"
                      : "Scan ID/Passport/License"}
                  </Button>
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  label="Vehicle Plate Number (optional)"
                  value={vehiclePlateNumber}
                  onChange={(e) => setVehiclePlateNumber(e.target.value)}
                />
              </Paper>
            )}

            <Box
              sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}
            >
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClear}
              >
                Clear
              </Button>

              <Button
                variant="contained"
                color={
                  invite && invite.currentStatus === "checked_in"
                    ? "warning"
                    : "primary"
                }
                onClick={handleVerify}
                disabled={
                  isMarkingAsScanned ||
                  isUploadingScans ||
                  Boolean(invite?.revoked)
                }
              >
                {isMarkingAsScanned || isUploadingScans ? (
                  <CircularProgress size={24} color="inherit" />
                ) : invite && invite.currentStatus === "checked_in" ? (
                  "Check Out Visitor"
                ) : (
                  "Check In Visitor"
                )}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={captureMode !== null}
        onClose={cancelCapture}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {captureMode === "id" ? "Scan Visitor ID" : "Take Visitor Photo"}
          <IconButton onClick={cancelCapture} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {captureError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {captureError}
            </Alert>
          )}

          <Box
            sx={{
              position: "relative",
              width: "100%",
              minHeight: 280,
              borderRadius: 2,
              overflow: "hidden",
              backgroundColor: "#000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isInitializingCaptureCamera && (
              <CircularProgress sx={{ position: "absolute", color: "#fff" }} />
            )}
            {capturedPreviewUrl ? (
              <img
                src={capturedPreviewUrl}
                alt="Captured preview"
                style={{ width: "100%", display: "block" }}
              />
            ) : (
              <video
                ref={captureVideoRef}
                autoPlay
                playsInline
                muted
                style={{ width: "100%", display: "block" }}
              />
            )}
          </Box>
          <canvas ref={captureCanvasRef} style={{ display: "none" }} />

          {captureError && (
            <Button
              sx={{ mt: 2 }}
              variant="text"
              onClick={() => {
                cancelCapture();
                if (captureMode === "id") idScanInputRef.current?.click();
                else photoInputRef.current?.click();
              }}
            >
              Choose File Instead
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          {capturedPreviewUrl ? (
            <>
              <Button onClick={retakeCapture}>Retake</Button>
              <Button variant="contained" onClick={confirmCapture}>
                Use Photo
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              startIcon={<PhotoCameraIcon />}
              onClick={takeSnapshot}
              disabled={isInitializingCaptureCamera || Boolean(captureError)}
            >
              Capture
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScanInvite;
